import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Store, Truck } from 'lucide-react';
import clsx from 'clsx';

import { PartnerPill } from '@/components/PartnerPill';
import { apiErrorMessage, apiGet } from '@/lib/api';
import { readBackState } from '@/lib/back-nav';
import { formatDate, formatDateTime, formatKop } from '@/lib/format';
import { telHref } from '@/lib/phone';
import { pluralDays } from '@/lib/subscription';
import {
  KIND,
  LEGAL_FORM_LABEL,
  MODERATION_LABEL,
  PAYMENT_STATUS_LABEL,
  type PartnerCardData,
  type PartnerKind,
} from '@/lib/partners';

/** Карточка поставщика или оптовика: контакты, подписка, активность, оплаты. Только просмотр. */
export default function PartnerCard({ kind }: { kind: PartnerKind }) {
  const cfg = KIND[kind];
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const back = readBackState(location.state);
  const [data, setData] = useState<PartnerCardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setError(null);
    void apiGet<PartnerCardData>(`${cfg.path}/${id}`, { silent: true })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          setError(apiErrorMessage(err, 'Не удалось открыть карточку'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id, cfg.path]);

  const backHref = back?.backTo ?? cfg.path;
  const backLabel = back?.backLabel ?? cfg.back;
  const goBack = (e: React.MouseEvent) => {
    if (!back) return;
    e.preventDefault();
    navigate(-back.backSteps);
  };
  const backLink = (
    <Link to={backHref} onClick={goBack} className="inline-flex items-center gap-1.5 text-sm text-ink/55 hover:text-ink">
      <ArrowLeft size={16} /> {backLabel}
    </Link>
  );

  if (error) {
    return (
      <div className="max-w-3xl space-y-3">
        <div className="card p-4 text-sm text-ink/70">{error}</div>
        {backLink}
      </div>
    );
  }
  if (!data || data.id !== id) return <div className="text-sm text-ink/50">Загрузка…</div>;

  const tel = telHref(data.phone);
  const Icon = kind === 'supplier' ? Truck : Store;
  const daysTone =
    data.accessState === 'blocked' ? 'text-rose-600' : (data.daysLeft ?? 99) <= 3 ? 'text-amber-600' : 'text-ink';

  return (
    <div className="max-w-3xl space-y-4">
      {backLink}

      <div className="card p-4 sm:p-5">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-line bg-paper">
            {data.logoUrl ? (
              <img src={data.logoUrl} alt={data.name} className="h-full w-full object-contain" />
            ) : (
              <Icon size={22} className="text-ink/25" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs text-ink/45">{cfg.one}</div>
            <h1 className="text-lg font-semibold leading-tight sm:text-xl">{data.name}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <PartnerPill row={data} />
              <span className={clsx('text-sm font-medium', daysTone)}>
                {data.accessState === 'blocked'
                  ? 'Доступ закрыт'
                  : data.daysLeft == null
                    ? 'Без срока'
                    : `${data.daysLeft} ${pluralDays(data.daysLeft)}`}
              </span>
              {data.status !== 'active' ? (
                <span
                  className={clsx(
                    'rounded-md px-2 py-0.5 text-[11px] font-medium',
                    data.status === 'pending' ? 'bg-amber-50 text-amber-800' : 'bg-rose-50 text-rose-700',
                  )}
                >
                  {MODERATION_LABEL[data.status]}
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2 border-t border-line/60 pt-4">
          {tel && data.phone ? (
            <a href={tel} className="btn border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
              <Phone size={16} className="shrink-0" /> {data.phone}
            </a>
          ) : null}
          <a href={`mailto:${data.email}`} className="btn max-w-full border border-line bg-white text-ink/75 hover:bg-paper">
            <Mail size={16} className="shrink-0" /> <span className="truncate">{data.email}</span>
          </a>
        </div>
      </div>

      <div className="card divide-y divide-line/60">
        <div className="px-4 py-3 text-sm font-medium">Подписка</div>
        <Row label="Статус" value={<PartnerPill row={data} />} />
        <Row label="Доступ до" value={formatDate(data.accessEndsAt)} />
        {data.trialEndsAt ? <Row label="Триал до" value={formatDate(data.trialEndsAt)} /> : null}
        <Row label="Стоимость" value={`${formatKop(data.priceKopecks)} / месяц`} />
        <Row label="Автопродление" value={data.autoRenew ? 'включено — карта сохранена' : 'нет'} />
      </div>

      <div className="card divide-y divide-line/60">
        <div className="px-4 py-3 text-sm font-medium">{kind === 'supplier' ? 'Компания' : 'Профиль'}</div>
        {kind === 'supplier' ? (
          <>
            <Row
              label="Юрлицо"
              value={
                data.legalName
                  ? `${data.legalForm ? `${LEGAL_FORM_LABEL[data.legalForm] ?? data.legalForm} ` : ''}${data.legalName}`
                  : null
              }
            />
            <Row label="ИНН" value={data.inn ?? null} />
            <Row label="Контактное лицо" value={data.contactPerson ?? null} />
            <Row label="Склад" value={data.warehouseAddress ?? null} />
            <Row label="Доставка" value={data.deliveryRegions?.length ? data.deliveryRegions.join(', ') : null} />
          </>
        ) : null}
        <Row label="Город" value={data.city} />
        <Row label="Зарегистрирован" value={formatDate(data.createdAt)} />
        <Row label="Последний вход" value={data.lastLoginAt ? formatDateTime(data.lastLoginAt) : 'ещё не входил'} />
        {data.description ? <div className="px-4 py-3 text-sm text-ink/70">{data.description}</div> : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {kind === 'supplier' ? (
          <>
            <Stat label="Товаров в каталоге" value={data.counts.products ?? 0} />
            <Stat label="Заявок от заведений" value={data.counts.orders ?? 0} />
          </>
        ) : (
          <>
            <Stat label="Товаров" value={data.counts.items ?? 0} />
            <Stat label="Продаж" value={data.counts.sales ?? 0} />
            <Stat label="Курьеров" value={data.counts.couriers ?? 0} />
          </>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="border-b border-line px-4 py-3 text-sm font-medium">Оплаты подписки</div>
        {data.payments.length === 0 ? (
          <div className="px-4 py-6 text-sm text-ink/50">Оплат ещё не было.</div>
        ) : (
          <ul className="divide-y divide-line/60">
            {data.payments.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <div className="font-medium tabular-nums">{formatKop(p.amountKopecks)}</div>
                  <div className="mt-0.5 text-xs text-ink/50">
                    {formatDate(p.paidAt ?? p.createdAt)}
                    {p.method ? ` · ${p.method}` : ''}
                  </div>
                </div>
                <span
                  className={clsx(
                    'shrink-0 text-xs font-medium',
                    p.status === 'succeeded'
                      ? 'text-emerald-700'
                      : p.status === 'refunded' || p.status === 'failed'
                        ? 'text-rose-600'
                        : 'text-ink/50',
                  )}
                >
                  {PAYMENT_STATUS_LABEL[p.status] ?? p.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-3">
      <div className="text-sm text-ink/55">{label}</div>
      <div className="min-w-0 break-words text-right text-sm">
        {value == null || value === '' ? <span className="text-ink/35">не указано</span> : <span className="font-medium">{value}</span>}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="card p-4">
      <div className="text-xs text-ink/50">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}
